import _compact from 'lodash/compact';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { TemplateType } from 'constants/templateTypes';
import { selectUserProjectIsUuidBased } from 'core/userContext';
import {
  MessageMedium,
  MetaApprovalStatusDefinition,
  PushContentDefinition,
  WhatsAppContentDefinition,
} from 'modules/graphql/types';
import { usePlacementSelectorContext } from 'modules/placements/context/PlacementSelectorContext';
import { useDisplayTestMessageStatus } from 'modules/templateEditor/hooks/useDisplayTestMessageStatus';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { Badge, BADGE_VARIANTS } from 'ui/badge';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ChevronDown16 } from 'ui/icons';
import { spacing } from 'ui/theme';
import { BottomTooltip, LeftTooltip } from 'ui/tooltip';

import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils';
import { AllMessageMedium } from 'modules/domains/campaign';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { TestMsgModals, useSendTestMsgContext } from '../../context/SendTestMsgContext';
import { useTemplateQueryContext } from '../../context/TemplateQueryContext';
import { displayContent } from '../content';
import { MessagingPermissionOptions } from '../types';
import {
  useGetCanManageBaseTemplates,
  useGetHasUserProfilePermission,
  useGetMessagingPermission,
} from '../utils';

const StyledBadge = styled(Badge)`
  margin-left: ${spacing.half};
`;

const DisplayOptionWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

export const useTestMessageOptions = () => {
  const { onSendTestToSelf, setOpenTestMsgModal } = useSendTestMsgContext();
  const { isDisplayingForSelf, internalListSizeDisplaying, otherAddressesDisplaying } =
    useDisplayTestMessageStatus();
  const { medium } = useTemplateQueryContext();
  const isUuidBased = useSelector(selectUserProjectIsUuidBased);
  const { isDirty, hasContent, liveTemplate } = useEditableFieldsContext();
  const { campaignId, experimentFlow: isExperimentFlow } = useQueryParamContext();
  const isSilentPushEnabled =
    medium === MessageMedium.Push &&
    (liveTemplate.mediumData as PushContentDefinition)?.isSilentPush === true;
  const isCampaignFlow = !!campaignId;
  const messagingPermission = useGetMessagingPermission();
  const canManageBaseTemplates = useGetCanManageBaseTemplates();
  const isSendProofDisabled =
    isCampaignFlow || isExperimentFlow
      ? messagingPermission === MessagingPermissionOptions.VIEW_ONLY
      : !canManageBaseTemplates;
  const sendProofTooltipContent =
    isCampaignFlow || isExperimentFlow
      ? displayContent.tooltipViewOnlyPermission
      : displayContent.tooltipManageBaseTemplatesPermission;
  const isSendAsRandomUserDisabled = !useGetHasUserProfilePermission();
  const { isSelectedPlacementArchived } = usePlacementSelectorContext();

  const isWhatsApp = getIsWhatsAppCampaign(medium as AllMessageMedium);

  const isEligibility = medium === EmbeddedMessage;
  const whatsAppApprovalStatus =
    medium === WhatsApp &&
    (liveTemplate.mediumData as WhatsAppContentDefinition)?.metaApprovalStatus;
  const canSendWhatsApp =
    !isWhatsApp || whatsAppApprovalStatus === MetaApprovalStatusDefinition.Approved;

  const isSendTestMessageButtonDisabled =
    isDirty ||
    (!hasContent && !isSilentPushEnabled) ||
    isSelectedPlacementArchived ||
    !canSendWhatsApp;

  const getDisabledSendTestMessageTooltipContent = () => {
    if (whatsAppApprovalStatus === MetaApprovalStatusDefinition.Pending)
      return displayContent.sendTestMessageWhatsAppPendingTooltipContent;
    if (whatsAppApprovalStatus === MetaApprovalStatusDefinition.Rejected)
      return displayContent.sendTestMessageWhatsAppRejectedTooltipContent;
    if (!canSendWhatsApp) return displayContent.sendTestMessageTooltipContent;
    return '';
  };

  // Embedded is the only channel supporting eligibility
  const sendMsgOptions: ActionMenuOption[] = _compact([
    {
      as: AllowedActionMenuElements.Button,
      content: displayContent.btnSendTestToSelf,
      id: 'to-yourself',
      onClick: onSendTestToSelf,
    },
    !isWhatsApp && {
      as: AllowedActionMenuElements.Button,
      content: (
        <BottomTooltip isEnabled={isSendProofDisabled} tooltipContent={sendProofTooltipContent}>
          {displayContent.btnSendTestToList}
        </BottomTooltip>
      ),
      id: 'to-an-internal-list',
      isDisabled: isSendProofDisabled,
      onClick: () => setOpenTestMsgModal(TestMsgModals.SendTestToInternalList),
    },
    !isWhatsApp && {
      as: AllowedActionMenuElements.Button,
      content: (
        <BottomTooltip
          isEnabled={isSendAsRandomUserDisabled || isSendProofDisabled}
          tooltipContent={
            isSendAsRandomUserDisabled
              ? displayContent.tooltipUserProfilePermission
              : sendProofTooltipContent
          }
        >
          {displayContent.btnSendTestAsRandom}
        </BottomTooltip>
      ),
      id: 'as-random-users',
      isDisabled: isSendAsRandomUserDisabled || isSendProofDisabled,
      onClick: () => setOpenTestMsgModal(TestMsgModals.SendTestAsRandom),
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <BottomTooltip isEnabled={isSendProofDisabled} tooltipContent={sendProofTooltipContent}>
          {isUuidBased
            ? displayContent.btnSendTestToOtherUuid
            : displayContent.btnSendTestToOtherAddress}
        </BottomTooltip>
      ),
      id: 'to-another-address',
      isDisabled: isSendProofDisabled,
      onClick: () => setOpenTestMsgModal(TestMsgModals.SendTestToOther),
    },
  ]);

  const displayMsgOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <DisplayOptionWrapper>
          {displayContent.btnSendTestToSelf}
          {isDisplayingForSelf && <StyledBadge variant={BADGE_VARIANTS.COUNTER}>1</StyledBadge>}
        </DisplayOptionWrapper>
      ),
      id: 'display-to-yourself',
      isDisabled: isSendProofDisabled,
      onClick: () => setOpenTestMsgModal(TestMsgModals.DisplayTestToYourself),
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <BottomTooltip isEnabled={isSendProofDisabled} tooltipContent={sendProofTooltipContent}>
          <DisplayOptionWrapper>
            {displayContent.btnSendTestToList}
            {!!internalListSizeDisplaying && (
              <StyledBadge variant={BADGE_VARIANTS.COUNTER}>
                {internalListSizeDisplaying}
              </StyledBadge>
            )}
          </DisplayOptionWrapper>
        </BottomTooltip>
      ),
      id: 'display-to-an-intenal-list',
      isDisabled: isSendProofDisabled,
      onClick: () => setOpenTestMsgModal(TestMsgModals.DisplayTestToInternalList),
    },
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <BottomTooltip isEnabled={isSendProofDisabled} tooltipContent={sendProofTooltipContent}>
          <DisplayOptionWrapper>
            {isUuidBased
              ? displayContent.btnSendTestToOtherUuid
              : displayContent.btnSendTestToOtherAddress}
            {!!otherAddressesDisplaying?.length && (
              <StyledBadge variant={BADGE_VARIANTS.COUNTER}>
                {otherAddressesDisplaying?.length}
              </StyledBadge>
            )}
          </DisplayOptionWrapper>
        </BottomTooltip>
      ),
      id: 'display-to-another-address',
      isDisabled: isSendProofDisabled,
      onClick: () => setOpenTestMsgModal(TestMsgModals.DisplayTestToOther),
    },
  ];

  return {
    isSendTestMessageButtonDisabled,
    disabledSendTestMessageTooltipContent: getDisabledSendTestMessageTooltipContent(),
    isEligibility,
    options: isEligibility ? displayMsgOptions : sendMsgOptions,
  };
};

export const SendTestMessage = ({
  menuAlignment,
  buttonSize = BUTTON_SIZES.LARGE,
  isEligibilityCampaignEnded = false,
}: {
  menuAlignment?: MENU_ALIGNMENTS;
  buttonSize?: BUTTON_SIZES;
  isEligibilityCampaignEnded?: boolean;
}) => {
  const { totalSizeDisplaying } = useDisplayTestMessageStatus();
  const { medium, templateType } = useTemplateQueryContext();
  const {
    options,
    isSendTestMessageButtonDisabled,
    isEligibility,
    disabledSendTestMessageTooltipContent,
  } = useTestMessageOptions();

  // Only WhatsApp user initiated templates should not support test messages
  const isWhatsAppUserInitiatedType =
    medium === WhatsApp && templateType === TemplateType.UserInitiated;

  const getActionMenuLabel = () => {
    if (medium === MessageMedium.Email) return displayContent.btnSendProof;
    else if (isEligibility) return displayContent.btnDisplayMessage;
    else return displayContent.btnSendTestMsg;
  };

  return isWhatsAppUserInitiatedType ? null : (
    <LeftTooltip
      isEnabled={isSendTestMessageButtonDisabled && !!disabledSendTestMessageTooltipContent}
      tooltipContent={disabledSendTestMessageTooltipContent}
    >
      <ActionMenu
        data-test="send-test-message-action-menu"
        align={menuAlignment || MENU_ALIGNMENTS.RIGHT}
        isDisabled={isSendTestMessageButtonDisabled || isEligibilityCampaignEnded}
        options={options}
      >
        <Button
          isDisabled={isSendTestMessageButtonDisabled || isEligibilityCampaignEnded}
          size={buttonSize}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          <span data-test="send-test-message-button-text">{getActionMenuLabel()}</span>
          {!!totalSizeDisplaying && !isEligibilityCampaignEnded && (
            <StyledBadge variant={BADGE_VARIANTS.COUNTER}>{totalSizeDisplaying}</StyledBadge>
          )}
          <ChevronDown16 />
        </Button>
      </ActionMenu>
    </LeftTooltip>
  );
};
